import { Fragment, type ReactNode, type RefObject, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GraphCounts } from './memory-graph.ts';
import type { ToolLogEntry } from './useAgentChat.ts';
import type { TreeNode } from '../../agents/userFilesApi.ts';
import type { MemorySnapshotSection } from '../../agents/agentMemoryApi.ts';
import type { ToolDoc } from '../../agents/toolCatalog.ts';
import MemoryGraph from './MemoryGraph.tsx';
import MemoryList from './MemoryList.tsx';
import ToolLog from './ToolLog.tsx';
import ToolsCatalog from './ToolsCatalog.tsx';
import McpServersView from './McpServersView.tsx';
import FilesPanel from './FilesPanel.tsx';
import type { ImageJob } from '../../agents/imageJobsApi.ts';
import JobsPanel from './JobsPanel.tsx';
import type { JobDefinition } from '../../agents/jobsApi.ts';
import type { WorkflowSummary } from '../../agents/workflowsApi.ts';
import type { CapConflict } from './useJobsData.ts';
import './InspectorPanel.css';

type SectionKey = 'memory' | 'tools' | 'files' | 'jobs';
/** A scoped section shows the live conversation view ("session"), the agent's
 *  all-up view ("all"), or — Tools only, for the MCP agent — the connected MCP
 *  servers ("mcp"). Which segments a section offers is set per-section via
 *  `scopes`; Memory only ever uses session/all. */
type Scope = 'session' | 'mcp' | 'all';

/** Human label for each scope segment in the header toggle. */
const SCOPE_LABEL: Record<Scope, string> = {
  session: 'Session',
  mcp: 'MCP',
  all: 'All',
};

/** The Tools header count: the catalog size in "All", the live call count in
 *  "Session", and nothing in "MCP" (that view carries its own server count). */
function toolsCount(scope: Scope, allLen: number, sessionLen: number): number | undefined {
  if (scope === 'all') {
    return allLen;
  }
  if (scope === 'session') {
    return sessionLen;
  }
  return undefined;
}

/** Most sections open at once on desktop. Opening a third evicts the
 *  least-recently-opened (the first entry in the ordered open list). */
const MAX_OPEN = 2;

interface InspectorPanelProps {
  canvasRef: RefObject<HTMLCanvasElement>;
  popRef: RefObject<HTMLDivElement>;
  counts: GraphCounts;
  toolLog: ToolLogEntry[];
  /** The agent's stored memory, grouped by tier (Memory › "All"). */
  memorySections: readonly MemorySnapshotSection[];
  /** The tools the agent can call (Tools › "All"). */
  tools: readonly ToolDoc[];
  filesTree: readonly TreeNode[];
  filesLoading: boolean;
  filesError: string | null;
  onOpenFile: (path: string) => void;
  onDeleteFile: (path: string) => void;
  /** Jobs section (U12/U13) — rendered only for agents with workflow tools
   *  (`showJobs`), i.e. Vera. All optional so harnesses/tests that don't
   *  exercise Jobs stay untouched; the console passes the `useJobsData`
   *  wiring. */
  showJobs?: boolean;
  jobsWorkflows?: readonly WorkflowSummary[];
  jobsSchedules?: readonly JobDefinition[];
  jobsImages?: readonly ImageJob[];
  jobsLoading?: boolean;
  jobsError?: string | null;
  jobsCapConflict?: CapConflict | null;
  onOpenWorkflow?: (workflowId: string, scrollToRuns?: boolean) => void;
  onRunWorkflow?: (workflow: WorkflowSummary) => void;
  onToggleSchedule?: (jobId: string, enabled: boolean) => void;
  /** Fired when the Jobs section transitions closed → open. The console uses
   *  it to refresh still-generating image jobs the moment they're on screen. */
  onJobsShown?: () => void;
  /** Fired when the Files section transitions closed → open. The tree is
   *  fetched once on mount, so without this a file the agent created mid-
   *  conversation only appears after a full page reload (the panel is closed
   *  by default, and not every file-producing tool is a `doc_*` call the chat
   *  can detect). */
  onFilesShown?: () => void;
  /** Guest console (tiers T8): locked tool rows, unlock CTAs for Files and
   *  the Memory "All" tiers (approved mock: padlock INSIDE the button). */
  guest?: boolean;
  /** When set (MCP agent + eligible user), the Tools "All" view shows the
   *  "MCP · Connected Tools" group linking to `/agent/<mcpSlug>/mcp`. */
  mcpSlug?: string;
  /** Optional unit at the top of the right rail (the guest/free desktop ad).
   *  The parent decides eligibility; the panel just renders it. */
  topSlot?: ReactNode;
}

/** The approved unlock affordance: one bordered CTA button, padlock inside. */
function GuestUnlock({ label }: { label: string }) {
  return (
    <div className="om-guest-unlock">
      <Link to="/auth/signup" className="om-guest-unlock__btn">
        <span aria-hidden="true">🔒</span> {label}
      </Link>
    </div>
  );
}

/** Right rail: a collapsible accordion of Memory, Tools, and Files. Memory and
 *  Tools each carry a Session ↔ All scope toggle in their header — Session is
 *  the conversation-scoped live view (graph / tool-use log), All is the agent's
 *  all-up view (tiered memory list / available-tools catalog). On desktop up to
 *  two sections share the rail, but switching a section to All maximizes it to
 *  the full height (collapsing the others); returning to Session restores the
 *  shared Session views. On mobile the bottom tab bar selects which section
 *  shows; the body still honors data-open so the left caret can collapse it. */
export default function InspectorPanel({
  canvasRef,
  popRef,
  counts,
  toolLog,
  memorySections,
  guest = false,
  tools,
  filesTree,
  filesLoading,
  filesError,
  onOpenFile,
  onDeleteFile,
  showJobs = false,
  jobsWorkflows = [],
  jobsSchedules = [],
  jobsImages = [],
  jobsLoading = false,
  jobsError = null,
  jobsCapConflict = null,
  onOpenWorkflow = () => undefined,
  onRunWorkflow = () => undefined,
  onToggleSchedule = () => undefined,
  onJobsShown = () => undefined,
  onFilesShown = () => undefined,
  mcpSlug,
  topSlot,
}: InspectorPanelProps) {
  // Ordered list of open sections, oldest-opened first. Defaults to Memory +
  // Tools open, Files closed.
  const [open, setOpen] = useState<SectionKey[]>(['memory', 'tools']);
  const [scope, setScope] = useState<{ memory: Scope; tools: Scope }>({
    memory: 'session',
    tools: 'session',
  });

  const toggleOpen = (key: SectionKey): void => {
    if (key === 'jobs' && !open.includes('jobs')) {
      onJobsShown();
    }
    if (key === 'files' && !open.includes('files')) {
      onFilesShown();
    }
    setOpen((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      const next = [...prev, key];
      // Evict the least-recently-opened until within the cap.
      return next.length > MAX_OPEN ? next.slice(next.length - MAX_OPEN) : next;
    });
  };

  const setSectionScope = (key: 'memory' | 'tools', next: Scope): void => {
    const siblingScope = key === 'memory' ? scope.tools : scope.memory;
    setScope((prev) => ({ ...prev, [key]: next }));
    // Picking a scope also expands the section. A list view ("All" or "MCP")
    // needs the full rail, so it maximizes (collapses the others); two compact
    // "Session" views can coexist, so returning to Session reopens a sibling.
    setOpen(() => {
      if (next !== 'session') {
        return [key];
      }
      const list: SectionKey[] = [key];
      const sibling: 'memory' | 'tools' = key === 'memory' ? 'tools' : 'memory';
      if (siblingScope === 'session') {
        list.push(sibling);
      }
      return list;
    });
  };

  const memoryRecordCount = memorySections.reduce((n, s) => n + s.records.length, 0);

  return (
    <aside className="om-panel om-panel--inspector">
      {topSlot}
      <div className="om-inspector__split">
        <AccordionSection
          sectionKey="memory"
          tick="◈"
          title="Memory"
          count={scope.memory === 'all' ? memoryRecordCount : counts.nodes}
          open={open.includes('memory')}
          onToggleOpen={toggleOpen}
          scope={scope.memory}
          onSetScope={(s) => setSectionScope('memory', s)}
        >
          <div className="om-scope-view om-scope-view--session">
            <MemoryGraph canvasRef={canvasRef} popRef={popRef} counts={counts} />
          </div>
          <div className="om-scope-view om-scope-view--all">
            {guest ? (
              <>
                <MemoryList sections={memorySections} />
                <p className="om-guest-unlock__note">
                  Session memory only — cleared when you leave.
                </p>
                <GuestUnlock label="Unlock Episodic & Semantic Memory" />
              </>
            ) : (
              <MemoryList sections={memorySections} />
            )}
          </div>
        </AccordionSection>

        <AccordionSection
          sectionKey="tools"
          tick="⚙"
          title="Tools"
          count={toolsCount(scope.tools, tools.length, toolLog.length)}
          open={open.includes('tools')}
          onToggleOpen={toggleOpen}
          scope={scope.tools}
          scopes={mcpSlug !== undefined ? ['session', 'mcp', 'all'] : ['session', 'all']}
          onSetScope={(s) => setSectionScope('tools', s)}
        >
          <div className="om-scope-view om-scope-view--session">
            <ToolLog entries={toolLog} />
          </div>
          {mcpSlug !== undefined ? (
            <div className="om-scope-view om-scope-view--mcp">
              <McpServersView slug={mcpSlug} />
            </div>
          ) : null}
          <div className="om-scope-view om-scope-view--all">
            <ToolsCatalog tools={tools} guest={guest} />
          </div>
        </AccordionSection>

        <AccordionSection
          sectionKey="files"
          tick="▤"
          title="Files"
          open={open.includes('files')}
          onToggleOpen={toggleOpen}
        >
          {guest ? (
            <GuestUnlock label="Create Account to Unlock Files Access" />
          ) : (
            <FilesPanel
              tree={filesTree}
              loading={filesLoading}
              error={filesError}
              onOpenFile={onOpenFile}
              onDeleteFile={onDeleteFile}
            />
          )}
        </AccordionSection>

        {showJobs ? (
          <AccordionSection
            sectionKey="jobs"
            tick="⛁"
            title="Jobs"
            count={jobsWorkflows.length + jobsSchedules.length + jobsImages.length}
            open={open.includes('jobs')}
            onToggleOpen={toggleOpen}
          >
            <JobsPanel
              workflows={jobsWorkflows}
              schedules={jobsSchedules}
              imageJobs={jobsImages}
              loading={jobsLoading}
              error={jobsError}
              capConflict={jobsCapConflict}
              onOpenWorkflow={onOpenWorkflow}
              onRunWorkflow={onRunWorkflow}
              onToggleSchedule={onToggleSchedule}
              onOpenImageFile={onOpenFile}
            />
          </AccordionSection>
        ) : null}
      </div>
    </aside>
  );
}

interface AccordionSectionProps {
  sectionKey: SectionKey;
  tick: string;
  title: string;
  /** Count shown after the title, e.g. node/record/call counts. Omitted at 0. */
  count?: number;
  open: boolean;
  onToggleOpen: (key: SectionKey) => void;
  /** Present only on scoped sections (Memory / Tools); drives the scope toggle
   *  and which child scope-view shows (via `data-scope`). */
  scope?: Scope;
  /** The segments the toggle offers, in order. Defaults to `['session','all']`;
   *  the Tools section passes `['session','mcp','all']` for the MCP agent. */
  scopes?: readonly Scope[];
  onSetScope?: (scope: Scope) => void;
  /** For scoped sections: the `.om-scope-view` children (one per scope). For
   *  Files: the section body. */
  children: ReactNode;
}

function AccordionSection({
  sectionKey,
  tick,
  title,
  count,
  open,
  onToggleOpen,
  scope,
  scopes = ['session', 'all'],
  onSetScope,
  children,
}: AccordionSectionProps) {
  const scoped = scope !== undefined && onSetScope !== undefined;
  return (
    <section
      className={`om-acc om-acc--${sectionKey}`}
      data-open={open}
      {...(scoped ? { 'data-scope': scope } : {})}
    >
      {/* The whole bar toggles the section; the title is also a button so the
          toggle is keyboard-reachable. The scope pills stop propagation so a
          scope click doesn't also toggle the section. */}
      <div
        className="om-acc__head om-acc__head--inline om-panel__head"
        onClick={() => onToggleOpen(sectionKey)}
      >
        <span className="om-acc__caret" aria-hidden="true">
          ▾
        </span>
        <span className="tick" aria-hidden="true">
          {tick}
        </span>
        <button
          type="button"
          className="om-acc__title om-acc__titlebtn"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            onToggleOpen(sectionKey);
          }}
        >
          {title}
        </button>
        {count !== undefined && count > 0 ? <span className="om-acc__badge">({count})</span> : null}
        {scoped ? (
          <span className="om-acc__inline-scope" onClick={(e) => e.stopPropagation()}>
            {scopes.map((s, i) => (
              <Fragment key={s}>
                {i > 0 ? (
                  <span className="om-acc__inline-sep" aria-hidden="true">
                    |
                  </span>
                ) : null}
                <button
                  type="button"
                  className={`om-acc__inline-btn${scope === s ? ' is-active' : ''}`}
                  aria-pressed={scope === s}
                  onClick={() => onSetScope(s)}
                >
                  {SCOPE_LABEL[s]}
                </button>
              </Fragment>
            ))}
          </span>
        ) : null}
      </div>
      <div className="om-acc__body">
        {scoped ? <div className="om-scope-views">{children}</div> : children}
      </div>
    </section>
  );
}
